// src/ContentContext.jsx
//
// Site copy, merged.
//
// The defaults in content.js render immediately and the API is fetched after
// mount, swapping in whatever has been edited in the portal. Three reasons it
// works this way rather than waiting for the response:
//
//   Nothing blocks first paint, so a slow API costs nothing.
//   A crawler sees the built-in copy without waiting on a second request.
//   An unreachable API leaves the site intact rather than blank.
//
// Merge rules: objects recurse, arrays replace wholesale. An array is a list
// the editor owns entirely - merging by index would leave a deleted row
// showing because the default still had one there.

import React, { createContext, useContext, useEffect, useState } from "react";
import { DEFAULTS } from "./content";

const SITE = "cold_storage";
const API = process.env.REACT_APP_API_URL;

const Ctx = createContext(DEFAULTS);

function merge(base, over) {
  if (Array.isArray(over)) return over;

  if (over && typeof over === "object" && !Array.isArray(base)) {
    const out = { ...base };
    for (const [k, v] of Object.entries(over)) {
      out[k] = k in base ? merge(base[k], v) : v;
    }
    return out;
  }

  return over === undefined || over === null || over === "" ? base : over;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let alive = true;

    fetch(`${API}/v1/content/${SITE}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d?.doc) return;
        setContent(merge(DEFAULTS, d.doc));
      })
      .catch(() => {
        // Silent. The page is already rendered with the defaults and there is
        // nothing a visitor could do about it.
      });

    return () => {
      alive = false;
    };
  }, []);

  return <Ctx.Provider value={content}>{children}</Ctx.Provider>;
}

export function useContent() {
  return useContext(Ctx);
}